<template>
  <div class="skill-picker gm-panel gm-panel--flush">
    <header class="skill-picker__head">
      <input
        v-model="query"
        type="search"
        class="gm-input skill-picker__search"
        placeholder="Search skills…"
        aria-label="Search skills"
      />
    </header>

    <div v-if="!groups.length" class="gm-empty skill-picker__empty">
      <p class="gm-empty__hint">
        {{ skills.length ? 'No skill matches that search.' : 'This school teaches no skills.' }}
      </p>
    </div>

    <ul v-else class="skill-picker__list gm-scroll">
      <li v-for="group in groups" :key="group.grade" class="grade">
        <div class="grade__head">
          <span class="grade__number">{{ group.grade }}</span>
          <span class="grade__tier">{{ group.tier }}</span>
          <span class="grade__count">{{ group.skills.length }}</span>
        </div>

        <button
          v-for="skill in group.skills"
          :key="skill.id"
          type="button"
          class="skill"
          :class="{ 'is-active': skill.id === selectedId }"
          :style="{ '--type-accent': typeAccent(skill.type) }"
          :title="skill.description"
          @click="$emit('select', skill.id)"
        >
          <GlyphIcon :src="skill.icon" :label="skill.name" size="1.9rem" />
          <span class="skill__body">
            <span class="skill__name">{{ skill.name }}</span>
            <span class="skill__meta">
              <span class="skill__type">{{ skill.type }}</span>
              <span v-if="skill.impact.length !== 1" class="skill__impacts">
                {{ skill.impact.length }} impacts
              </span>
            </span>
          </span>
        </button>
      </li>
    </ul>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import GlyphIcon from './GlyphIcon.vue';
import { groupByGrade } from '@/constants/grades.js';

const props = defineProps({
  skills: { type: Array, required: true },
  selectedId: { type: [Number, String], default: null },
});

defineEmits(['select']);

const query = ref('');

// Switching school with a stale query would show an empty list and read as a
// school with no skills.
watch(() => props.skills, () => { query.value = ''; });

const TYPE_ACCENTS = {
  attack: '#e0603f',
  defense: '#7fa8c9',
  heal: '#8fb63c',
  buff: '#f0c75e',
  debuff: '#b06fc9',
  utility: '#9a9078',
  special: '#00d9ff',
  summon: '#5fbf9f',
};

function typeAccent(type) {
  return TYPE_ACCENTS[type] || 'var(--gm-text-muted)';
}

const groups = computed(() => {
  const term = query.value.trim().toLowerCase();
  const matches = term
    ? props.skills.filter(
        (skill) =>
          skill.name.toLowerCase().includes(term) ||
          skill.description.toLowerCase().includes(term),
      )
    : props.skills;
  return groupByGrade(matches);
});
</script>

<style scoped>
.skill-picker {
  display: flex;
  flex-direction: column;
  min-height: 0;
  width: 16rem;
  flex: 0 0 auto;
}

.skill-picker__head {
  padding: var(--gm-gap-sm);
  border-bottom: 1px solid var(--gm-border-dim);
}

.skill-picker__search {
  font-size: 0.78rem;
  padding: 0.35rem 0.5rem;
}

.skill-picker__empty {
  padding: var(--gm-gap-lg) var(--gm-gap);
}

.skill-picker__list {
  flex: 1;
  min-height: 0;
  margin: 0;
  padding: 0 var(--gm-gap-xs) var(--gm-gap-sm);
  list-style: none;
}

.grade__head {
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  align-items: baseline;
  gap: 0.4rem;
  padding: 0.45rem 0.4rem 0.25rem;
  background: var(--gm-surface-1);
  border-bottom: 1px solid var(--gm-border-dim);
}

.grade__number {
  font-family: var(--gm-font-display);
  font-size: 0.72rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--gm-accent);
}

.grade__tier {
  flex: 1;
  font-family: var(--gm-font-display);
  font-size: 0.58rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--gm-text-muted);
}

.grade__count {
  font-size: 0.58rem;
  font-variant-numeric: tabular-nums;
  color: var(--gm-text-dim);
}

.skill {
  width: 100%;
  display: flex;
  align-items: center;
  gap: var(--gm-gap-sm);
  margin-top: 0.15rem;
  padding: 0.3rem 0.35rem;
  background: transparent;
  border: 1px solid transparent;
  border-left: 2px solid transparent;
  border-radius: var(--gm-radius-sm);
  color: var(--gm-text-muted);
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.skill:hover {
  background: var(--gm-accent-softer);
  color: var(--gm-text);
}

.skill.is-active {
  color: var(--gm-text);
  background: var(--gm-surface-2);
  border-color: var(--gm-border-dim);
  border-left-color: var(--type-accent);
}

.skill__body {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.skill__name {
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.skill__meta {
  display: flex;
  gap: 0.4rem;
  font-size: 0.6rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--gm-text-dim);
}

.skill__type {
  color: var(--type-accent);
}
</style>
